import { useSelector } from 'react-redux'
import { useEffect, useMemo } from 'react'
import {
  useGetPhotosQuery,
  useGetVideosQuery,
  useGetPanoramasQuery,
  useGetHotspotsByStopIdQuery,
  useGetPositionalMediaByStopIdQuery,
  useLazyGetPositionalMediaVideosQuery,
} from 'Services/institution'

export const useMediaDetails = (stopId: number) => {
  const { locId, instId } = useSelector((state: any) => state.location)

  /* Panoramas */
  const { data: panoramasData } = useGetPanoramasQuery(
    {
      instId,
      locId,
      stopId,
    },
    { skip: !locId || !instId || !stopId }
  )
  const allPanoramas = panoramasData?.resources?.data
  const sortedPanoramas =
    allPanoramas &&
    [...allPanoramas].sort(
      (a: any, b: any) =>
        a.panorama_stops[0]?.order - b.panorama_stops[0]?.order
    ).filter(
      (pano: any) => pano.status === '1'
    )
  const panoAssetUrl = panoramasData?.resources?.metadata?.path?.slice(
    0,
    panoramasData?.resources?.metadata?.path?.indexOf('/media')
  )

  /* Photos */
  const { data: photosData } = useGetPhotosQuery(
    {
      instId,
      locId,
      stopId,
    },
    { skip: !locId || !instId || !stopId }
  )
  const allPhotos = photosData?.resources?.data
  const sortedPhotos =
    allPhotos &&
    [...allPhotos].sort(
      (a: any, b: any) => a.photo_stops[0]?.order - b.photo_stops[0]?.order
    ).filter(photo => photo.status === '1')
  const photoAssetUrl = photosData?.resources?.metadata?.path?.youvisit?.slice(
    0,
    photosData?.resources?.metadata?.path?.youvisit.indexOf('/media')
  )

  /* Videos */
  const { data: videosData } = useGetVideosQuery(
    {
      instId,
      locId,
      stopId,
    },
    { skip: !locId || !instId }
  )
  const allVideos = videosData?.resources?.data
  const sortedVideos =
    allVideos &&
    [...allVideos].sort(
      (a: any, b: any) => a.video_stops[0]?.order - b.video_stops[0]?.order
    )
  const approvedVideos = sortedVideos?.filter(
    (video: any) =>
      (video.status === '1' && video.approved === '1') ||
      video.hotspotMediaId ||
      video.obj_type === 'positional_media'
  )

  /* Hotspots */
  const { data: { resources: hotspotsData = [] } = {} } =
    useGetHotspotsByStopIdQuery(
      {
        instId,
        locId,
        stopId,
      },
      { skip: !locId || !instId || !stopId }
    )

  const formatHotspots = (hotspots: any) => {
    return hotspots.map((hotspot: any) => {
      return {
        hotspotMediaId: hotspot.action?.item_id,
        title: hotspot.title,
        itemType: hotspot.action?.item_type,
        description: hotspot.media?.description || hotspot.action?.meta2 || hotspot.media?.accessibility_description,
        videoid: hotspot.videos[0]?.videoid,
        videoType: hotspot.videos[0]?.type,
        url: hotspot.videos[0]?.url,
        accessibility_description: hotspot.media?.accessibility_description
      }
    })
  }

  const photoHotspots = hotspotsData
    ?.filter((hotspot: any) => hotspot.status === 1)
    .filter((hotspot: any) => hotspot.action?.item_type === 'photo')
  const videoHotspots = hotspotsData
    ?.filter((hotspot: any) => hotspot.status === 1)
    .filter(
      (hotspot: any) =>
        hotspot.action?.item_type === 'video' ||
        hotspot.action?.item_type === '360 video'
    )
  const panoHotspots = hotspotsData
    ?.filter((hotspot: any) => hotspot.status === 1)
    .filter((hotspot: any) => hotspot.action?.item_type === 'panorama')
  const textHotspots = hotspotsData
    ?.filter((hotspot: any) => hotspot.status === 1)
    .filter((hotspot: any) => hotspot.action?.item_type === 'tooltip')

  /* Positional Media */
  const { data: { resources: positionalMediaData = [] } = {} } =
    useGetPositionalMediaByStopIdQuery(
      {
        instId,
        locId,
        stopId,
      },
      { skip: !locId || !instId || !stopId }
    )

  const photoPosMedia = positionalMediaData
    ?.filter((posMedia: any) => posMedia.status === 1)
    ?.filter((media: any) => media.type === 'photo')
  const panoPosMedia = positionalMediaData
    ?.filter((posMedia: any) => posMedia.status === 1)
    ?.filter((media: any) => media.type === 'panorama')
  const videoPosMedia = useMemo(
    () =>
      positionalMediaData
        ?.filter((posMedia: any) => posMedia.status === 1)
        ?.filter((media: any) => media.type === 'video'),
    [positionalMediaData]
  )

  // Need to make additional call to get video details for positional media videos
  const [
    triggerPosMediaVideos,
    { data: { resources: matchedVideoPosMedia = [] } = {} },
  ] = useLazyGetPositionalMediaVideosQuery()

  useEffect(() => {
    if (videoPosMedia.length > 0) {
      const videoIds = videoPosMedia?.map((video: any) => video.media_id)
      triggerPosMediaVideos({ instId, locId, videoIds })
    }
  }, [videoPosMedia])

  const combinePosMediaVideoData = (posMediaData: any, videoData: any) => {
    if (!videoData) return []
    return videoData.map((video: any) => {
      const matchedVideo = posMediaData?.find(
        (posMediaVideo: any) => posMediaVideo.media_id === video.id
      )
      return {
        ...matchedVideo,
        type: video.type,
        videoid: video.videoid,
        url: video.url,
      }
    })
  }

  return {
    photos: sortedPhotos,
    photoAssetUrl,
    videos: approvedVideos,
    panoramas: sortedPanoramas,
    panoAssetUrl,
    photoHotspots: formatHotspots(photoHotspots),
    videoHotspots: formatHotspots(videoHotspots),
    panoHotspots: formatHotspots(panoHotspots),
    textHotspots: formatHotspots(textHotspots),
    photoPosMedia,
    videoPosMedia: combinePosMediaVideoData(
      videoPosMedia,
      matchedVideoPosMedia?.data
    ),
    panoPosMedia,
  }
}
